/**
 * F5 — medición del marketplace /order: tracker anónimo del lado del cliente.
 *
 * Diseño (anexo v2.1 del plan de marketplace):
 *  · `makeTracker()` es una factory PURA — recibe `send` y `sessionId` como
 *    dependencias, así la máquina cola/flush se testea sin DOM ni red.
 *  · Cola con flush por tres vías: timer de 4s, tope de 20 eventos, o
 *    `flush()` manual (el caller lo cablea a `pagehide`/unmount).
 *  · JAMÁS lanza: es telemetría best-effort; un fallo aquí no puede tumbar
 *    el directorio ni bloquear una navegación.
 *  · Identidad = uuid anónimo en sessionStorage (sin cookie, sin PII): vive
 *    lo que la pestaña, suficiente para de-duplicar impresiones y medir CTR.
 */

export type MarketplaceEventName = "impression" | "card_click" | "search" | "filter" | "tracker_open"

export type MarketplaceTrackEvent = {
  event: MarketplaceEventName
  /** locations.id del merchant (cards); ausente en search/filter. */
  location_id?: string
  /** Dónde pasó: "grid" | "suggestion" | "hero"… */
  placement?: string
  /** Contexto pequeño y anónimo ({q}, {cuisine, city, open}). */
  meta?: Record<string, unknown>
}

/** `E` es el tipo de evento. Default = el del marketplace, así los callers
 *  existentes no cambian; la telemetría de sugerencias lo instancia con el suyo. */
export type TrackPayload<E = MarketplaceTrackEvent> = {
  session_id: string
  events: E[]
}

export type Tracker<E = MarketplaceTrackEvent> = {
  /** Encola un evento (flush automático por timer o por tope de cola). */
  track: (event: E) => void
  /** Manda lo pendiente ya (pagehide / unmount). No-op con cola vacía. */
  flush: () => void
  /** Tamaño actual de la cola (tests/debug). */
  pending: () => number
}

type TrackerDeps<E> = {
  sessionId: string
  send: (payload: TrackPayload<E>) => void
  /** Ventana del flush automático. Default 4000ms. */
  flushDelayMs?: number
  /** Tope de cola: al llegar aquí se manda sin esperar el timer. Default 20. */
  maxQueue?: number
}

export function makeTracker<E = MarketplaceTrackEvent>({
  sessionId,
  send,
  flushDelayMs = 4000,
  maxQueue = 20,
}: TrackerDeps<E>): Tracker<E> {
  let queue: E[] = []
  let timer: ReturnType<typeof setTimeout> | null = null

  function flush(): void {
    try {
      if (timer !== null) {
        clearTimeout(timer)
        timer = null
      }
      if (queue.length === 0) return
      // Drenar ANTES de mandar: si send lanza no re-encolamos (sin loops de
      // reintento — perder un batch de telemetría es aceptable, un loop no).
      const events = queue
      queue = []
      send({ session_id: sessionId, events })
    } catch {
      // jamás throw
    }
  }

  function track(event: E): void {
    try {
      queue.push(event)
      if (queue.length >= maxQueue) {
        flush()
        return
      }
      if (timer === null) {
        timer = setTimeout(flush, flushDelayMs)
      }
    } catch {
      // jamás throw
    }
  }

  return { track, flush, pending: () => queue.length }
}

/* ────────────────────────── identidad de sesión ────────────────────────── */

const SESSION_KEY = "redy_mk_sid"

/** Interfaz mínima de storage — sessionStorage real o un doble en tests. */
type MinimalStorage = Pick<Storage, "getItem" | "setItem">

export function randomId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID()
    }
  } catch {
    // sigue el fallback
  }
  // Fallback sin crypto (webviews viejos): suficiente para de-dup anónimo.
  return `mk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

/**
 * uuid anónimo por PESTAÑA (sessionStorage): sin cookie, sin PII, muere al
 * cerrar. Si el storage lanza (Safari private, cuota) devuelve un id efímero —
 * la sesión no de-duplica entre recargas, pero JAMÁS lanza.
 */
export function getSessionId(
  storage?: MinimalStorage,
): string {
  let store = storage
  try {
    store = store ?? (typeof sessionStorage !== "undefined" ? sessionStorage : undefined)
  } catch {
    store = undefined
  }
  try {
    if (store) {
      const existing = store.getItem(SESSION_KEY)
      if (existing && existing.length >= 8 && existing.length <= 64) return existing
      const fresh = randomId()
      store.setItem(SESSION_KEY, fresh)
      return fresh
    }
  } catch {
    // storage roto → efímero
  }
  return randomId()
}

/* ─────────────── "una impresión por card por sesión" ─────────────── */

const SEEN_KEY = "redy_mk_seen"
const SEEN_CAP = 500

export type OnceGate = {
  has: (id: string) => boolean
  add: (id: string) => void
}

/**
 * Gate de de-duplicación por sesión, respaldado en sessionStorage para que una
 * recarga no vuelva a contar las mismas impresiones. Con storage roto degrada
 * a memoria (de-dup solo dentro del pageview) sin lanzar.
 */
export function makeOnceGate(storage?: MinimalStorage): OnceGate {
  let store = storage
  try {
    store = store ?? (typeof sessionStorage !== "undefined" ? sessionStorage : undefined)
  } catch {
    store = undefined
  }
  let seen = new Set<string>()
  try {
    const raw = store?.getItem(SEEN_KEY)
    if (raw) {
      const parsed: unknown = JSON.parse(raw)
      if (Array.isArray(parsed)) seen = new Set(parsed.filter((v): v is string => typeof v === "string"))
    }
  } catch {
    // basura o storage roto → arranca vacío
  }
  return {
    has: (id) => seen.has(id),
    add: (id) => {
      try {
        if (seen.has(id)) return
        seen.add(id)
        if (store && seen.size <= SEEN_CAP) {
          store.setItem(SEEN_KEY, JSON.stringify([...seen]))
        }
      } catch {
        // persistir es best-effort; el Set en memoria sigue de-duplicando
      }
    },
  }
}

/* ───────────────────────────── transporte ───────────────────────────── */

type SenderDeps = {
  /** navigator.sendBeacon-like. Default: el del navegador si existe. */
  beacon?: (url: string, data: BodyInit) => boolean
  /** fetch-like para el fallback. Default: fetch global si existe. */
  fetchFn?: typeof fetch
}

/**
 * Transporte del batch: `sendBeacon` primero (sobrevive pagehide/navegación),
 * `fetch keepalive` de fallback. Nunca lanza, nunca espera respuesta — el
 * route responde 204 pase lo que pase, y aquí nadie lo lee.
 */
export function makeBeaconSender(
  url = "/api/marketplace/events",
  deps: SenderDeps = {},
): (payload: TrackPayload) => void {
  return (payload) => {
    let body: string
    try {
      body = JSON.stringify(payload)
    } catch {
      return
    }
    const beacon =
      deps.beacon ??
      (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function"
        ? navigator.sendBeacon.bind(navigator)
        : undefined)
    if (beacon) {
      try {
        // Blob application/json: same-origin, el route hace req.json().
        const data = typeof Blob !== "undefined" ? new Blob([body], { type: "application/json" }) : body
        if (beacon(url, data)) return
      } catch {
        // cae al fetch
      }
    }
    const fetchFn = deps.fetchFn ?? (typeof fetch !== "undefined" ? fetch : undefined)
    if (!fetchFn) return
    try {
      void fetchFn(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {
        // best-effort: el batch se pierde y ya
      })
    } catch {
      // jamás throw
    }
  }
}
